import { useEffect, useState } from "react";
import gsap from "gsap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { viewPresentation } from "../../redux/features/presentations/presentationsSlice";
import Navbar from "../Navbar";
import Footer from "../Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { Film } from "lucide-react";
import { useAnimation } from "../AnimateTab/AnimationContext";
import { SlideCanvasRenderer } from "../SlideCanvasRenderer";

export default function PreviewPresentation() {
  const { uuid } = useParams();
  const dispatch = useDispatch();
  const [slides, setSlides] = useState([]);
  const [activeSlide, setActiveSlide] = useState(null);

  const { selectedPresentation, getPresentationLoading } = useSelector(
    (state) => state.presentation,
  );

  const { runGSAPPreview, setElements, setInteractions, elements } =
    useAnimation();

  useEffect(() => {
    if (uuid) {
      dispatch(viewPresentation(uuid));
    }
  }, [uuid, dispatch]);

  useEffect(() => {
    if (selectedPresentation && selectedPresentation.slides) {
      setSlides([]);

      const formattedSlides = selectedPresentation.slides.map((slide, idx) => {
        const slideType = slide.type || slide.type; // Handle snake_case from backend

        let parsedAnimationData = null;

        if (slide.animation_data) {
          try {
            const parsed =
              typeof slide.animation_data === "string"
                ? JSON.parse(slide.animation_data)
                : slide.animation_data;

            parsedAnimationData = {
              ...parsed,
              elements: (parsed?.elements || []).filter(Boolean),
              interactions: (parsed?.interactions || []).filter(Boolean),
            };
          } catch (_) {
            console.error(
              "Failed to parse animation_data for slide:",
              slide.id,
            );
          }
        }

        if (slideType === "blank") {
          const canvasBackgroundColor =
            parsedAnimationData?.canvasBackgroundColor || "#1a1a2e";
          return {
            id: slide.id,
            title: slide.title,
            is_active: slide.is_active !== false,
            type: "blank",
            canvasBackgroundColor,
            animation_data: parsedAnimationData, // <-- Added here
            with_animation: slide.with_animation,
          };
        } else if (
          slideType === "question" ||
          (slide.question && !slide.image)
        ) {
          return {
            id: slide.id,
            type: slide.question.question_type,
            title: slide.title,
            is_active: slide.is_active !== false,
            question: slide.question.question_text,
            // choices: slide.question.choices.map((c) => c.choice_text),
            choices: slide.question?.choices.map((c, index) => ({
              id: c.id,
              choice_text: c.choice_text,
              order: c.order,
            })),
            animation_data: parsedAnimationData, // <-- Added here
            with_animation: slide.with_animation,
          };
        } else if (slideType === "image" || slide.image) {
          let url = slide.image?.image_url;

          if (url && !url.startsWith("http") && !url.startsWith("blob:")) {
            try {
              const apiUrl =
                process.env.REACT_APP_API_URL || "http://localhost:49152";
              const origin = new URL(apiUrl).origin;
              url = `${origin}${url.startsWith("/") ? "" : "/"}${url}`;
            } catch (e) {
              console.error("Error parsing API URL:", e);
              url = `${process.env.REACT_APP_API_URL || "http://localhost:49152"}${url}`;
            }
          }

          return {
            id: slide.id,
            is_active: slide.is_active !== false,
            type: "image",
            title: slide.image.title || slide.title,
            imageUrl: url,
            animation_data: parsedAnimationData, // <-- Added here
            with_animation: slide.with_animation,
          };
        }

        return {
          ...slide,
          is_active: slide.is_active !== false,
          animation_data: parsedAnimationData, // <-- Safe fallback inclusion
          with_animation: slide.with_animation,
        };
      });

      setSlides(formattedSlides);
      setActiveSlide(0);
    }
  }, [selectedPresentation]);

  useEffect(() => {
    if (activeSlide === null || !slides[activeSlide]) return;

    const slide = slides[activeSlide];

    if (!slide.with_animation || !slide.animation_data) return;

    // Every animated slide type (question / image / blank) runs through the
    // same canvas pipeline — positioned elements animate natively (incl. move).

    const { elements: els = [], interactions: ints = [] } =
      slide.animation_data;

    const slideStart = ints.find((int) => int.event === "slide_start");

    setElements(els);
    setInteractions(ints.filter(Boolean));

    let tl = null;
    const timer = setTimeout(() => {
      runGSAPPreview();
    }, 50);
    return () => clearTimeout(timer);
  }, [activeSlide, elements]);

  if (getPresentationLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500 text-lg">
        Loading presentation...
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-lightPurple/5">
      <Navbar />
      <Link to="/presentations">
        <button className="p-2 text-sm rounded-lg bg-purple-400 hover:bg-purple-500 text-white ml-8 mt-6 font-bold w-24 uppercase">
          <FontAwesomeIcon icon={faArrowRotateLeft} className="mr-1" />
          Back
        </button>
      </Link>
      <div className="flex-grow">
        <h1 className="text-center font-semibold text-3xl my-2">
          🧾Preview Presentation
        </h1>
        <div className="grid grid-cols-12 gap-4 p-8 ">
          {/* Left Panel: Slides List */}
          <div
            className="col-span-3 bg-white border-r border-gray-200 p-4 overflow-y-auto rounded-2xl h-[44vw]"
            style={{ scrollbarWidth: "thin" }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Slides</h2>
              <span className="text-gray-500 text-sm">
                {slides.length} slides
              </span>
            </div>

            {/* Slides List */}
            {slides.map((slide, index) => (
              <div
                key={slide.id}
                onClick={() => setActiveSlide(index)}
                className={`mb-3 rounded-lg cursor-pointer transition border overflow-hidden relative ${
                  activeSlide === index
                    ? "border-2 border-lightPurple shadow-md bg-purple-50"
                    : "border border-gray-200 hover:border-gray-400"
                } ${!slide.is_active ? "opacity-60 grayscale-[0.5]" : ""}`}
              >
                {!slide.is_active && (
                  <div className="absolute top-1 left-1 bg-gray-500 text-white text-[10px] px-1.5 py-0.5 rounded-md font-bold z-10 flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
                    HIDDEN
                  </div>
                )}
                <div className="bg-gray-50 w-full h-40 flex items-center justify-center overflow-hidden rounded-lg border border-gray-200">
                  {slide.imageUrl && (
                    <img
                      src={slide.imageUrl}
                      alt={slide.title}
                      className="object-contain w-full h-full"
                    />
                  )}
                  {slide.type === "blank" && (
                    <div
                      className="w-full h-full flex flex-col items-center justify-center gap-1.5"
                      style={{
                        backgroundColor:
                          slide.canvasBackgroundColor || "#1a1a2e",
                      }}
                    >
                      <Film className="w-5 h-5 text-white/40" />
                      <span className="text-[10px] text-white/40 font-semibold uppercase tracking-wide">
                        Animated
                      </span>
                    </div>
                  )}
                  {(slide.type === "multiple" || slide.type === "poll") && (
                    <div
                      className="p-2 w-full"
                      style={{
                        backgroundColor:
                          slide.animation_data?.canvasBackgroundColor ||
                          "#f3f4f6",
                        ...(slide.animation_data?.canvasBackground
                          ? {
                              backgroundImage: `url("${slide.animation_data?.canvasBackground}")`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }
                          : {}),
                      }}
                    >
                      <p className="font-bold text-base mb-2 ml-1 text-black">
                        {slide.title}
                      </p>
                      <h2 className="text-base font-bold mb-2 ml-1">
                        {slide.question}
                      </h2>
                      <ul>
                        {slide?.choices?.map((choice, i) => (
                          <li
                            key={i}
                            className="p-2 border rounded-md hover:bg-gray-100 cursor-pointer flex items-center space-x-2 my-2"
                          >
                            <input
                              readOnly
                              type={
                                slide.type === "poll" ? "radio" : "checkbox"
                              }
                              name={`choice-${slide.id}`}
                              id={`choice-${i}`}
                              value={choice}
                              className={`h-2 w-2 ${
                                slide.type === "poll"
                                  ? "form-radio"
                                  : "form-checkbox"
                              } text-lightPurple`}
                            />
                            <label
                              htmlFor={`choice-${i}`}
                              className="text-gray-700"
                            >
                              {choice.choice_text}
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {slide.type === "text" && (
                    <div
                      className="p-2 w-full "
                      style={{
                        backgroundColor:
                          slide.animation_data?.canvasBackgroundColor ||
                          "#f3f4f6",
                        ...(slide.animation_data?.canvasBackground
                          ? {
                              backgroundImage: `url("${slide.animation_data?.canvasBackground}")`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }
                          : {}),
                      }}
                    >
                      <p className="font-bold text-base mb-2 ml-1 text-black">
                        {slide.title}
                      </p>
                      <h2 className="text-base font-bold mb-2 ml-1">
                        {slide.question}
                      </h2>
                      <textarea
                        readOnly
                        placeholder="Answer..."
                        className="w-full border rounded p-1 text-[11px] resize-none h-10"
                      />
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between bg-white px-2 py-1 text-[11px] text-gray-600">
                  <span className="font-medium">{index + 1}</span>
                  <span className="truncate">{slide.title}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Middle Panel: Slide Preview */}
          <div className="col-span-9 bg-white rounded-2xl shadow p-6 flex items-center justify-center h-[44vw]">
            <div className="border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden relative shrink-0 w-full aspect-video">
              {activeSlide !== null &&
                slides[activeSlide] &&
                !slides[activeSlide].is_active && (
                  <div className="absolute top-4 left-4 bg-gray-500 text-white text-[11px] px-2 py-1 rounded-md font-bold z-30 flex items-center gap-1.5 shadow-md">
                    <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-pulse"></div>
                    HIDDEN
                  </div>
                )}

              <div
                className={`w-full h-full relative ${activeSlide !== null && slides[activeSlide] && !slides[activeSlide].is_active ? "opacity-40 grayscale-[0.7]" : ""}`}
                style={{
                  containerType: "inline-size",
                  ...(activeSlide !== null && slides[activeSlide]
                    ? (() => {
                        const slide = slides[activeSlide];
                        const bgImg =
                          slide?.animation_data?.canvasBackground ||
                          slide?.imageUrl;
                        const bgColor =
                          slide?.animation_data?.canvasBackgroundColor ||
                          slide?.canvasBackgroundColor ||
                          (slide?.type === "blank" ? "#1a1a2e" : "transparent");
                        return bgImg
                          ? {
                              backgroundImage: `url("${bgImg}")`,
                              backgroundSize: "100% 100%",
                              backgroundRepeat: "no-repeat",
                              backgroundPosition: "center",
                              backgroundColor: bgColor,
                            }
                          : { backgroundColor: bgColor };
                      })()
                    : {}),
                }}
              >
                {activeSlide !== null && slides.length > 0 ? (
                  <>
                    {slides[activeSlide].type === "blank" &&
                      !slides[activeSlide]?.animation_data
                        ?.canvasBackground && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 pointer-events-none"></div>
                      )}

                    {(slides[activeSlide].type === "image" ||
                      selectedPresentation?.canvasBackground) && (
                      <div className="w-full h-full relative">
                        <img
                          src={
                            slides[activeSlide].canvasBackground ||
                            slides[activeSlide].imageUrl
                          }
                          alt={slides[activeSlide].title || "Slide Background"}
                          className="w-full h-full "
                          style={{
                            backgroundColor:
                              slides[activeSlide].canvasBackgroundColor ||
                              "transparent",
                          }}
                        />
                      </div>
                    )}

                    {slides[activeSlide].type === "blank" && (
                      <div className="h-[30rem] w-full">
                        <div
                          className="w-full h-full flex flex-col items-center justify-center gap-2"
                          style={{
                            backgroundColor:
                              slides[activeSlide].canvasBackgroundColor ||
                              "#1a1a2e",
                          }}
                        ></div>
                      </div>
                    )}

                    {/* QUESTION TYPES (poll / multiple / text) */}
                    {["poll", "multiple", "text"].includes(
                      slides[activeSlide].type,
                    ) &&
                      (() => {
                        const slide = slides[activeSlide];
                        const hasCanvas =
                          (slide?.animation_data?.elements || []).length > 0;

                        if (hasCanvas) {
                          const bgColor =
                            slide.animation_data?.canvasBackgroundColor ||
                            "#f3f4f6";
                          const bgImage =
                            slide.animation_data?.canvasBackground;
                          return (
                            <div
                              className="absolute inset-0"
                              style={{
                                backgroundColor: bgColor,
                                ...(bgImage
                                  ? {
                                      backgroundImage: `url("${bgImage}")`,
                                      backgroundSize: "cover",
                                      backgroundPosition: "center",
                                    }
                                  : {}),
                              }}
                            >
                              <SlideCanvasRenderer slide={slide} />
                            </div>
                          );
                        }

                        // Fallback — no canvas data (read-only preview).
                        return (
                          <div className="p-6 w-full">
                            <p className="font-bold text-xl mb-4 text-black">
                              {slide.title}
                            </p>
                            <h2 className="text-xl font-bold mb-4">
                              {slide.question}
                            </h2>
                            {slide.type === "text" ? (
                              <textarea
                                readOnly
                                placeholder="Answer..."
                                className="w-full border rounded p-2 min-h-[100px] bg-white"
                              />
                            ) : (
                              <ul>
                                {slide?.choices?.map((choice, i) => (
                                  <li
                                    key={i}
                                    className="p-2 border rounded-md flex items-center space-x-2 my-3 bg-white"
                                  >
                                    <input
                                      readOnly
                                      type={
                                        slide.type === "poll"
                                          ? "radio"
                                          : "checkbox"
                                      }
                                      className="h-4 w-4 text-lightPurple"
                                    />
                                    <label className="text-gray-700">
                                      {choice.choice_text}
                                    </label>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })()}

                    {/* RENDER ANIMATED OVERLAY ELEMENTS (non-question slides) */}
                    {!["poll", "multiple", "text"].includes(
                      slides[activeSlide].type,
                    ) &&
                      slides[activeSlide]?.animation_data?.elements?.map(
                        (element) => (
                          <div
                            id={`canvas-el-${element.id}`}
                            key={element.id}
                            className="absolute pointer-events-none"
                            style={{
                              left: `${Number(element.state?.position?.x || 0)}%`,
                              top: `${Number(element.state?.position?.y || 0)}%`,
                              opacity: Number(element.state?.opacity ?? 1),
                              transform: `rotate(${Number(element.state?.rotation || 0)}deg) scale(${Number(element.state?.scale || 1)})`,
                              zIndex: 70,
                              width: `${Number(element.state?.size?.w || 20)}%`,
                              height: `${Number(element.state?.size?.h || 20)}%`,
                              display: "flex",
                              alignItems:
                                element.type === "text"
                                  ? "flex-start"
                                  : "center",
                              justifyContent: "center",
                            }}
                          >
                            {element.type === "text" && (
                              <div className="w-full h-full flex items-start px-2">
                                <h2
                                  className="font-extrabold tracking-tight pointer-events-none leading-tight"
                                  style={{
                                    fontSize: `${(Number(element.fontSize) || 28) / 10}cqi`,
                                    color: element.color || "#ffffff",
                                  }}
                                >
                                  {element.text}
                                </h2>
                              </div>
                            )}

                            {element.type === "image" &&
                              (element.src ? (
                                <img
                                  src={element.src}
                                  alt=""
                                  className="w-full h-full object-contain rounded"
                                />
                              ) : (
                                <div className="w-full h-full border border-dashed border-purpleColor/60 bg-purpleColor/10 rounded flex items-center justify-center">
                                  <span className="text-purpleColor/70 text-xs font-semibold uppercase tracking-wide px-2 text-center">
                                    {element.name}
                                  </span>
                                </div>
                              ))}

                            {element.type === "button" && (
                              <button className="w-full h-full bg-purpleColor hover:bg-[#7a1b51] text-white font-bold text-sm rounded-lg shadow-md flex items-center justify-center px-4 pointer-events-none">
                                {element.text}
                              </button>
                            )}
                          </div>
                        ),
                      )}
                  </>
                ) : (
                  <span className="text-gray-500">Slide Preview</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
